import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useBoardCore } from "../../../../cores/boards";
import { useIntl } from "react-intl";
import { Label } from "reactstrap";
import TextInput from "./TextInput";
import { Field } from "../../../../cores/boards/interfaces/api/put-card";
import { CardCustomField } from "../../../../cores/boards/constants/custom-field-type";
import NumberInput from "./NumberInput";
import DateInput from "./DateInput";
import moment from "moment";
import MoneyInput from "./MoneyInput";
import { getCurrencySymbol } from "../../../../cores/boards/constants/currencies.constant";
import { CL$Format } from "../../../../cores/utils";
import { normalizeWebsiteUrl } from "../helpers/customFieldActions";
import { useOpenCustomFieldEmail } from "../helpers/openCustomFieldEmail";
import { CustomField } from "../../../../cores/boards/interfaces/api/get-custom-fields";

interface Props {
  canModify: boolean;
  cardId?: string | null;
  onSubmit: (arg0: Field) => void;
}

interface fieldValues {
  value: string;
  unit: string;
  edit: boolean;
}

export default function CustomFieldContainer({
  canModify,
  cardId = null,
  onSubmit,
}: Props) {
  const intl = useIntl();
  const { customFields, card } = useBoardCore();
  const openCustomFieldEmail = useOpenCustomFieldEmail(cardId);
  const [fields, setFields] = useState<{ [key: string]: fieldValues }>({});

  useEffect(() => {
    const newFields = customFields.reduce((prev: { [key: string]: fieldValues }, curr) => {
      const field = card?.customFields.find((field) => field.id === curr.id);
      const value = field ? field.value : "";
      const unit = field ? field.unit : "";
      prev[`${curr.id}`] = { value, unit, edit: false };
      return prev;
    }, {});
    setFields(newFields);
  }, [customFields, card]);

  const toggleEdition = useCallback(
    (field: string) => {
      if (canModify) {
        const newFields = { ...fields };
        newFields[`${field}`].edit = !newFields[`${field}`].edit;
        setFields(newFields);
      }
    },
    [fields, canModify]
  );

  const onSettingField = useCallback(
    (field: string, value: string, unit = "") => {
      const newFields = { ...fields };
      newFields[`${field}`].value = value;
      setFields(newFields);

      onSubmit({ id: field, value, unit });
      toggleEdition(field);
    },
    [fields, onSubmit, toggleEdition]
  );

  const showInput = useCallback(
    (field: CustomField) => {
      if (!fields[`${field.id}`]) return null;

      switch (field.type) {
        case CardCustomField.Text:
        case CardCustomField.Url:
        case CardCustomField.Email:
          return (
            <TextInput
              value={fields[`${field.id}`].value}
              onSubmit={(v) => onSettingField(field.id, v)}
              allowEmpty={true}
              showChek={true}
              toggleEdition={() => toggleEdition(field.id)}
            />
          );
        case CardCustomField.Number:
          return (
            <NumberInput
              value={parseFloat(fields[`${field.id}`].value)}
              onSubmit={(v) => onSettingField(field.id, `${v}`)}
              allowEmpty={true}
              showChek={true}
              toggleEdition={() => toggleEdition(field.id)}
            />
          );
        case CardCustomField.Date:
          return (
            <DateInput
              date={fields[`${field.id}`].value}
              onSubmit={(v) => onSettingField(field.id, moment(v).toISOString())}
              toggleEdition={() => toggleEdition(field.id)}
            />
          );
        case CardCustomField.Money:
          return (
            <MoneyInput
              value={fields[`${field.id}`].value}
              unit={fields[`${field.id}`].unit}
              allowEmpty={true}
              showChek={true}
              onSubmit={(v, u) => onSettingField(field.id, `${v}`, u)}
              toggleEdition={() => toggleEdition(field.id)}
            />
          );
        default:
          return null;
      }
    },
    [fields, toggleEdition, onSettingField]
  );

  const showLabel = useCallback(
    (field: CustomField) => {
      if (!fields[`${field.id}`]) return "";

      const { value, unit } = fields[`${field.id}`];

      if (field.type === CardCustomField.Date) {
        return moment(value).format("DD/MM/YYYY");
      }

      if (field.type === CardCustomField.Money) {
        return `${getCurrencySymbol(unit)} ${CL$Format(value)}`;
      }

      return `${unit} ${value}`.trim();
    },
    [fields]
  );

  const renderFieldValue = useCallback(
    (field: CustomField) => {
      const value = fields[`${field.id}`]?.value?.trim();

      if (!value) {
        return (
          <span className="text-muted">
            {intl.formatMessage({ id: "app.noData" })}
          </span>
        );
      }

      if (field.type === CardCustomField.Url) {
        const href = normalizeWebsiteUrl(value);

        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="crm-custom-field-link"
            title={intl.formatMessage({ id: "app.crmCustomField.openWebsite" })}
            onClick={(e) => e.stopPropagation()}
          >
            {value}
            <FontAwesomeIcon
              icon={["fas", "external-link-alt"]}
              className="ml-1"
              size="xs"
            />
          </a>
        );
      }

      if (field.type === CardCustomField.Email) {
        return (
          <button
            type="button"
            className="crm-custom-field-link crm-custom-field-link--button"
            title={intl.formatMessage({ id: "app.crmCustomField.sendEmail" })}
            onClick={(e) => {
              e.stopPropagation();
              openCustomFieldEmail(value);
            }}
          >
            {value}
            <FontAwesomeIcon icon={["fas", "envelope"]} className="ml-1" size="xs" />
          </button>
        );
      }

      return <span>{showLabel(field)}</span>;
    },
    [fields, intl, openCustomFieldEmail, showLabel]
  );

  return (
    <div className="d-flex flex-wrap w-100">
      {customFields.map(
        (field) =>
          fields[`${field.id}`] && (
            <div className="w-50 px-1 mt-4" key={field.id}>
              <Label className="font-weight-bold">{field.name}</Label>

              {!fields[`${field.id}`].edit ? (
                <div className="d-flex align-items-center crm-custom-field-value">
                  {!fields[`${field.id}`].value ? (
                    <div
                      className="pointer truncate flex-grow-1"
                      onClick={() => toggleEdition(field.id)}
                    >
                      {renderFieldValue(field)}
                    </div>
                  ) : field.type === CardCustomField.Url ||
                    field.type === CardCustomField.Email ? (
                    <>
                      <div className="truncate flex-grow-1 min-w-0">
                        {renderFieldValue(field)}
                      </div>
                      {canModify && (
                        <FontAwesomeIcon
                          icon={["fas", "edit"]}
                          size="sm"
                          className="ml-2 pointer text-muted crm-custom-field-edit"
                          title={intl.formatMessage({ id: "app.customField.update" })}
                          onClick={() => toggleEdition(field.id)}
                        />
                      )}
                    </>
                  ) : (
                    <div
                      className="pointer truncate flex-grow-1"
                      onClick={() => toggleEdition(field.id)}
                    >
                      {renderFieldValue(field)}
                    </div>
                  )}
                </div>
              ) : (
                showInput(field)
              )}
            </div>
          )
      )}
    </div>
  );
}
